import React, { useState, useEffect } from 'react'
import { Card, Button, Space, Typography, List, Tag, Slider, Switch, Row, Col, Tooltip, Progress, Alert } from 'antd'
import { BulbOutlined, AimOutlined, EyeOutlined, SettingOutlined, CheckCircleOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'
import SmartPlacementAI, { PlacementRecommendation, DEFAULT_PROPERTY_CONSTRAINTS } from '../../services/ai/SmartPlacementAI'

const { Title, Text } = Typography

interface SmartPlacementPanelProps {
  onClose: () => void
}

const SmartPlacementPanel: React.FC<SmartPlacementPanelProps> = ({ onClose }) => {
  const { getAllObjects, selectedObjects, addADUModel, addPoolModel, updateObjectProperties } = useDesignStore()
  const [recommendations, setRecommendations] = useState<PlacementRecommendation[]>([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [selectedObjectType, setSelectedObjectType] = useState<'ADU' | 'Pool'>('ADU')
  const [showRecommendations, setShowRecommendations] = useState(false)
  const [preferences, setPreferences] = useState({
    prioritizePrivacy: false,
    prioritizeAccess: true,
    prioritizeSunExposure: false,
    avoidUtilities: true
  })

  const allObjects = getAllObjects()
  const placementAI = new SmartPlacementAI(DEFAULT_PROPERTY_CONSTRAINTS, allObjects)

  // Generate recommendations
  const generateRecommendations = async () => {
    setIsAnalyzing(true)
    
    // Simulate AI processing time
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    const objectDimensions = selectedObjectType === 'ADU' 
      ? { width: 24, height: 12, depth: 32 } // Default ADU dimensions
      : { width: 16, height: 4, depth: 32 }  // Default pool dimensions
    
    const newRecommendations = placementAI.generatePlacementRecommendations(
      selectedObjectType,
      objectDimensions,
      preferences
    )
    
    setRecommendations(newRecommendations)
    setShowRecommendations(true)
    setIsAnalyzing(false)
  }

  // Apply a recommendation
  const applyRecommendation = (recommendation: PlacementRecommendation) => {
    if (selectedObjectType === 'ADU') {
      addADUModel({
        type: 'ADU',
        name: `Smart Placed ADU`,
        position: recommendation.position,
        rotation: recommendation.rotation,
        scale: [1, 1, 1],
        dimensions: { width: 24, height: 12, depth: 32 },
        materials: {
          walls: '#f5f5f5',
          roof: '#808080',
          door: '#8b4513',
          windows: '#87ceeb',
          trim: '#ffffff'
        },
        features: {
          bedrooms: 1,
          bathrooms: 1,
          kitchen: true,
          livingRoom: true,
          dining: false,
          office: false,
          parking: false,
          storage: true,
          laundry: true,
          porch: false,
          balcony: false,
          fireplace: false
        },
        sections: [],
        isModular: false
      })
    } else {
      addPoolModel({
        type: 'Pool',
        name: `Smart Placed Pool`,
        position: recommendation.position,
        rotation: recommendation.rotation,
        scale: [1, 1, 1],
        dimensions: { width: 16, length: 32, depth: 4 },
        shape: 'rectangular',
        materials: {
          shell: 'gunite',
          interior: 'plaster',
          interiorColor: '#4169E1',
          coping: 'concrete',
          copingColor: '#D3D3D3',
          deck: 'concrete',
          deckColor: '#F5F5DC',
          tile: 'ceramic',
          tileColor: '#87CEEB',
          water: '#006994'
        },
        features: {
          spa: false,
          waterfall: false,
          fountains: false,
          lighting: true,
          lightingType: 'led',
          lightingColor: 'white',
          heating: false,
          heatingType: 'gas',
          cooling: false,
          filtration: 'sand',
          automation: 'basic',
          cleaner: 'none',
          sanitization: 'chlorine',
          safety: true,
          safetyType: 'fence',
          cover: false,
          coverType: 'manual',
          sound: false,
          wifi: false,
          excavation: 'standard',
          access: 'easy'
        }
      })
    }
    
    // Close the panel after applying
    onClose()
  }

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'optimal': return '#52c41a'
      case 'good': return '#1890ff'
      case 'acceptable': return '#faad14'
      case 'warning': return '#ff4d4f'
      default: return '#d9d9d9'
    }
  }

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'optimal': return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'good': return <CheckCircleOutlined style={{ color: '#1890ff' }} />
      case 'acceptable': return <InfoCircleOutlined style={{ color: '#faad14' }} />
      case 'warning': return <WarningOutlined style={{ color: '#ff4d4f' }} />
      default: return <InfoCircleOutlined />
    }
  }

  return (
    <Card
      title={
        <Space>
          <AimOutlined />
          <span>Smart Placement AI</span>
        </Space>
      }
      extra={
        <Button type="text" onClick={onClose}>
          ×
        </Button>
      }
      style={{
        position: 'fixed',
        top: '80px',
        left: '20px',
        width: '450px',
        maxHeight: '85vh',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Object Type Selection */}
        <div>
          <Text strong style={{ marginBottom: '8px', display: 'block' }}>
            What would you like to place?
          </Text>
          <Space>
            <Button
              type={selectedObjectType === 'ADU' ? 'primary' : 'default'}
              onClick={() => setSelectedObjectType('ADU')}
              size="small"
            >
              🏠 ADU
            </Button>
            <Button
              type={selectedObjectType === 'Pool' ? 'primary' : 'default'}
              onClick={() => setSelectedObjectType('Pool')}
              size="small"
            >
              🏊 Pool
            </Button>
          </Space>
        </div>

        {/* Preferences */}
        <div>
          <Text strong style={{ marginBottom: '8px', display: 'block' }}>
            Placement Preferences
          </Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text>Prioritize Privacy</Text>
              </Col>
              <Col>
                <Switch
                  size="small"
                  checked={preferences.prioritizePrivacy}
                  onChange={(checked) => setPreferences(prev => ({ ...prev, prioritizePrivacy: checked }))}
                />
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <Text>Prioritize Access</Text>
              </Col>
              <Col>
                <Switch
                  size="small"
                  checked={preferences.prioritizeAccess}
                  onChange={(checked) => setPreferences(prev => ({ ...prev, prioritizeAccess: checked }))}
                />
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <Text>Prioritize Sun Exposure</Text>
              </Col>
              <Col>
                <Switch
                  size="small"
                  checked={preferences.prioritizeSunExposure}
                  onChange={(checked) => setPreferences(prev => ({ ...prev, prioritizeSunExposure: checked }))}
                />
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <Text>Avoid Utilities</Text>
              </Col>
              <Col>
                <Switch
                  size="small"
                  checked={preferences.avoidUtilities}
                  onChange={(checked) => setPreferences(prev => ({ ...prev, avoidUtilities: checked }))}
                />
              </Col>
            </Row>
          </Space>
        </div>

        {/* Generate Button */}
        <Button
          type="primary"
          icon={<BulbOutlined />}
          onClick={generateRecommendations}
          loading={isAnalyzing}
          block
          size="large"
        >
          {isAnalyzing ? 'Analyzing Property...' : 'Generate Smart Recommendations'}
        </Button>

        {/* Analysis Progress */}
        {isAnalyzing && (
          <div>
            <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
              🤖 AI is analyzing your property for optimal placement...
            </Text>
            <Progress percent={100} showInfo={false} status="active" />
            <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Checking setback requirements</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Evaluating privacy factors</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Analyzing access routes</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Assessing sun exposure</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Reviewing utility clearances</Text>
            </Space>
          </div>
        )}

        {/* Recommendations */}
        {showRecommendations && recommendations.length > 0 && (
          <div>
            <Text strong style={{ marginBottom: '8px', display: 'block' }}>
              🎯 Smart Placement Recommendations ({recommendations.length})
            </Text>
            <List
              size="small"
              dataSource={recommendations.slice(0, 5)} // Show top 5
              renderItem={(recommendation, index) => (
                <List.Item
                  style={{
                    border: `1px solid ${getCategoryColor(recommendation.category)}`,
                    borderRadius: '6px',
                    marginBottom: '8px',
                    padding: '12px',
                    backgroundColor: index === 0 ? 'rgba(82, 196, 26, 0.05)' : 'transparent'
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Space>
                        {getCategoryIcon(recommendation.category)}
                        <Text strong>
                          Option {index + 1}
                          {index === 0 && <Tag color="gold" style={{ marginLeft: '4px' }}>BEST</Tag>}
                        </Text>
                      </Space>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {Math.round(recommendation.confidence * 100)}% match
                      </Text>
                    </div>
                    
                    <Progress 
                      percent={Math.round(recommendation.confidence * 100)} 
                      size="small" 
                      strokeColor={getCategoryColor(recommendation.category)}
                      showInfo={false}
                      style={{ marginBottom: '8px' }}
                    />
                    
                    <Text style={{ fontSize: '12px', color: '#666', marginBottom: '8px', display: 'block' }}>
                      {recommendation.reasoning}
                    </Text>
                    
                    <div style={{ marginBottom: '8px' }}>
                      <Space wrap>
                        {recommendation.considerations.slice(0, 3).map((consideration, i) => (
                          <Tooltip key={i} title={consideration.description}>
                            <Tag 
                              color={consideration.impact === 'positive' ? 'green' : consideration.impact === 'negative' ? 'red' : 'default'}
                              style={{ fontSize: '10px' }}
                            >
                              {consideration.type.replace('_', ' ')}
                            </Tag>
                          </Tooltip>
                        ))}
                      </Space>
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={{ fontSize: '11px', color: '#999' }}>
                        Position: ({recommendation.position[0].toFixed(1)}, {recommendation.position[2].toFixed(1)})
                      </Text>
                      <Space>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            // TODO: Highlight position in 3D scene
                            console.log('Preview position:', recommendation.position)
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => applyRecommendation(recommendation)}
                        >
                          Apply
                        </Button>
                      </Space>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}

        {/* No recommendations message */}
        {showRecommendations && recommendations.length === 0 && (
          <Alert
            message="No suitable placements found"
            description="The current property constraints and preferences don't allow for optimal placement. Try adjusting your preferences or property setup."
            type="warning"
            showIcon
          />
        )}

        {/* Info Panel */}
        <div style={{ 
          background: '#f6f8fa', 
          padding: '12px', 
          borderRadius: '6px',
          border: '1px solid #e1e4e8'
        }}>
          <Text strong style={{ fontSize: '12px', color: '#586069' }}>
            🤖 How Smart Placement Works
          </Text>
          <br />
          <Text style={{ fontSize: '11px', color: '#586069' }}>
            • Analyzes setback requirements and zoning compliance
            <br />
            • Considers privacy, access, and sun exposure
            <br />
            • Evaluates utility clearances and drainage
            <br />
            • Provides confidence scores and detailed reasoning
            <br />
            • Learns from your preferences for better recommendations
          </Text>
        </div>
      </Space>
    </Card>
  )
}

export default SmartPlacementPanel
